import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  DEFAULT_ALLOWED_CASE_TYPES,
  ORDERED_CASE_TYPES,
} from '@app/constants/case-types';
import { useIsServiceProductEnabled } from '@app/helpers/featureFlags';
import { CaseTypeEnum } from '@app/types/create-case';
import { StateType } from '@app/types/reducer-state';

const useServiceCaseSelector = <T>(
  selector: (
    settings: NonNullable<
      StateType['organizations']['organizationSettings']['caseTypes'][CaseTypeEnum.service]
    >,
  ) => T,
  defaultValue: T,
): T =>
  useSelector((state: StateType) => {
    const settings =
      state.organizations?.organizationSettings?.caseTypes?.[
        CaseTypeEnum.service
      ];
    return settings ? selector(settings) : defaultValue;
  });

export const useServiceCaseSettingsEnabled = (): boolean =>
  useServiceCaseSelector(
    (settings) => ('enabled' in settings ? settings.enabled : false),
    false,
  );

export interface ServiceCaseStatus {
  isServiceCaseSettingsEnabled: boolean;
  isServiceProductEnabled: boolean;
  isServiceVisible: boolean;
}

export const useServiceCaseStatus = (): ServiceCaseStatus => {
  const isServiceCaseSettingsEnabled = useServiceCaseSettingsEnabled();
  const isServiceProductEnabled = useIsServiceProductEnabled();

  return useMemo(
    () => ({
      isServiceCaseSettingsEnabled,
      isServiceProductEnabled,
      isServiceVisible: isServiceCaseSettingsEnabled && isServiceProductEnabled,
    }),
    [isServiceCaseSettingsEnabled, isServiceProductEnabled],
  );
};

export const useServiceCaseEnabled = (): boolean => {
  const { isServiceVisible } = useServiceCaseStatus();
  return isServiceVisible;
};

/** Service gating mirrors isServiceEnabled in server/helpers/feature-flags.js. */
export const useAssignableCaseTypes = (): CaseTypeEnum[] => {
  const isServiceEnabled = useServiceCaseEnabled();

  return ORDERED_CASE_TYPES.filter(
    (caseType) => caseType !== CaseTypeEnum.service || isServiceEnabled,
  );
};

export const useAllowedCaseTypes = (): CaseTypeEnum[] =>
  useSelector(
    (state: StateType) =>
      state.auth?.user?.allowedCaseTypes ?? DEFAULT_ALLOWED_CASE_TYPES,
  );

/**
 * True when the current user may see/create `caseType`. An empty allowed list
 * means unrestricted, matching applyAllowedCaseTypesFiltering on the server.
 */
export const useIsCaseTypeAllowed = (): ((
  caseType: CaseTypeEnum,
) => boolean) => {
  const allowedCaseTypes = useAllowedCaseTypes();

  return (caseType: CaseTypeEnum) =>
    allowedCaseTypes.length === 0 || allowedCaseTypes.includes(caseType);
};

export const useVisibleCaseTypes = (): CaseTypeEnum[] => {
  const assignableCaseTypes = useAssignableCaseTypes();
  const isCaseTypeAllowed = useIsCaseTypeAllowed();

  return assignableCaseTypes.filter(isCaseTypeAllowed);
};
